import React, { useState } from 'react';
import { Box, Typography, Chip, Accordion, AccordionSummary, AccordionDetails, Divider, Icon, useTheme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Visitor } from './VisitRequestStatus';
import dayjs from 'dayjs';
import VisitRequestStatusPopup from './VisitRequestStatusPopup';
import { useTranslation } from 'react-i18next';

interface VisitorListProps {
  title: string;
  titleColor?: string;
  subTitle: string;
  expanded: boolean;
  visitors: Visitor[];
  handleOpenSecurityCardPopup: (card: Visitor) => void;
}
export default function VisitRequestStatusList({ title, titleColor, subTitle, expanded, visitors, handleOpenSecurityCardPopup }: VisitorListProps) {
  // 다크 라이트 모드
  const theme = useTheme();
  // 다국어
  const { t } = useTranslation();

  return (
    <Box
      borderRadius={1}
      bgcolor={theme.palette.contentBlock.background}
      padding={2}
      display="flex"
      flexDirection="column"
      gap={2}
      flexGrow={1}
      width="100%"
      maxWidth={{ sm: 'calc(50% - 8px)', xs: '100%' }}
      height="calc(100vh - 338px)"
      minHeight="200px"
    >
      <Typography variant="h5" fontWeight="bold" sx={{ color: titleColor }}>
        {title}
        {visitors.length > 0 && `(${visitors.length}${t('visit_request_status.count')})`}
        <span style={{ fontSize: '16px', color: theme.palette.mode === 'dark' ? '#cfcfcf' : '#5f5f5f', marginLeft: '10px' }}>{subTitle}</span>
      </Typography>
      <Box display="flex" flexDirection="column" gap={2} padding="2px" sx={{ overflowY: 'auto' }} flexGrow={1} height="100%">
        {!visitors || visitors.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1} height="100%" color={theme.palette.fieldText.title}>
            {t('visit_request_status.list.no_matching_visitors')}
          </Box>
        ) : (
          visitors.map((visitor, index) => (
            <Box
              key={index}
              borderRadius={1}
              bgcolor={theme.palette.contentBlock.main}
              boxShadow={1}
              padding={2}
              display="flex"
              flexDirection="column"
              gap={1}
              onClick={() => handleOpenSecurityCardPopup(visitor)}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? '#404040' : '#f0f0f0',
                  outline: '1px solid #ccc'
                }
              }}
            >
              <Box
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'start', sm: 'center' }}
                justifyContent="space-between"
                gap={1}
                flexWrap="wrap"
              >
                <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                  <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                    {t('visit_request_status.list.name')}
                  </Typography>
                  {visitor.isVip && (
                    <Chip
                      label="VIP"
                      size="small"
                      sx={{ backgroundColor: theme.palette.mode == 'dark' ? '#DBC000' : '#FFEB3B', color: 'black', userSelect: 'none' }}
                    />
                  )}

                  <Typography variant="h6" fontWeight="bold" color={theme.palette.primary.main}>
                    {visitor.name}
                  </Typography>
                  <Typography variant="caption" color={theme.palette.fieldText.title}>
                    {visitor.visitRequest.visitFrom}
                    {visitor.visitRequest.visitFrom != visitor.visitRequest.visitTo && ` ~ ${visitor.visitRequest.visitTo}`}
                  </Typography>
                </Box>
                {/* 출입보안카드 */}
                <Box display="flex" flexDirection="row" gap={1} alignItems="center" sx={{ border: '1px solid #ccc', borderRadius: '4px', padding: '2px 8px' }}>
                  <Typography variant="caption" color={theme.palette.fieldText.title} lineHeight={1.75}>
                    {t('visit_request_status.list.access_security_card')}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={theme.palette.primary.main}
                    sx={{ textDecoration: 'underline' }} // 텍스트에 밑줄 추가
                    // onClick={() => handleOpenSecurityCardPopup(visitor)}
                  >
                    {visitor.securityManageNumber}
                  </Typography>
                </Box>
              </Box>

              {/* 정보들 */}
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={0.5} flexWrap="wrap">
                  {/* 내방시간 */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                      {t('visit_request_status.list.visit_time')}
                    </Typography>

                    <Typography variant="body2" fontWeight="bold" sx={{ flexGrow: 1, width: '130px' }}>
                      {visitor.visitRequest.visitStartTime && visitor.visitRequest.visitEndTime
                        ? `${dayjs(visitor.visitRequest.visitStartTime, 'HH:mm:ss').format('HH:mm')} ~ ${dayjs(visitor.visitRequest.visitEndTime, 'HH:mm:ss').format('HH:mm')}`
                        : '-'}
                    </Typography>
                  </Box>
                  {/* 담당자 */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                      {t('visit_request_status.list.manager_name')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ flexGrow: 1, width: '130px' }}>
                      {visitor.visitRequest.managerName}
                    </Typography>
                  </Box>
                  {/* 차량번호 */}
                  <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                    <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                      {t('visit_request_status.list.car')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ flexGrow: 1, width: '130px' }}>
                      {visitor.carNumber}
                    </Typography>
                  </Box>
                  {/*</Box>*/}

                  {/*<Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 0.5, sm: 2 }} flexWrap="wrap">*/}
                  {/* 생년월일 */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                      {t('visit_request_status.list.birth')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ flexGrow: 1, width: '130px' }}>
                      {visitor.birth}
                    </Typography>
                  </Box>
                  {/* 전화번호 */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                      {t('visit_request_status.list.phone')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ flexGrow: 1, width: '130px' }}>
                      {visitor.phoneNumber}
                    </Typography>
                  </Box>
                  {/* 회사 */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                      {t('visit_request_status.list.company_name')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ flexGrow: 1, width: '130px' }}>
                      {visitor.companyName}
                    </Typography>
                  </Box>
                </Box>

                {/* 목적 */}
                <Box display="flex" flexDirection="column" gap={1} flexWrap="wrap">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                      {t('visit_request_status.list.purpose')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ flexGrow: 1, width: '130px' }}>
                      {visitor.visitRequest.purpose}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {/* 디지털기기 */}
              {visitor.digitalDevices && visitor.digitalDevices.length > 0 && (
                <Accordion
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  sx={{
                    backgroundColor: theme.palette.contentBlock.background,
                    borderRadius: '8px',
                    marginTop: '0 !important',
                    display: 'flex', // 플렉스 박스 사용
                    flexDirection: 'column',
                    boxShadow: 'none',
                    '&::before': {
                      display: 'none'
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                    sx={{
                      minHeight: '48px',
                      height: '48px',
                      '&.Mui-expanded': {
                        minHeight: '48px',
                        height: '48px'
                      },
                      '& .MuiAccordionSummary-content': {
                        margin: '0 !important'
                      }
                    }}
                  >
                    <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                      {t('visit_request_status.list.digital_devices')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {visitor.digitalDevices ? visitor.digitalDevices.length : '0'} {t('visit_request_status.list.mount')}
                    </Typography>
                  </AccordionSummary>
                  <Divider variant="fullWidth" sx={{ mx: 2 }} />
                  <AccordionDetails sx={{ padding: 2 }}>
                    {visitor.digitalDevices && visitor.digitalDevices.length > 0 ? (
                      visitor.digitalDevices.map((device, idx) => (
                        <Box key={idx} display="flex" alignItems="center" gap={1}>
                          <Icon sx={{ fontSize: '16px' }}>•</Icon>
                          <Typography variant="body2" fontWeight="bold">
                            {device.digitalDeviceName} ({device.modelName})
                          </Typography>
                          <Typography variant="caption" color={theme.palette.mode == 'dark' ? '#bfbfbf' : '#7f7f7f'}>
                            {device.serialNumber}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2">{t('visit_request_status.list.no_devices')}</Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}
